import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { aiApi } from '../../../api/ai.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { KpiCard } from '../../../components/ui/KpiCard.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

const KIND_TABS = [
  { value: '', label: 'Tutti' },
  { value: 'chat', label: 'Chat' },
  { value: 'embedding', label: 'Embedding' },
  { value: 'playground', label: 'Playground' },
  { value: 'transcription', label: 'Trascrizione' },
  { value: 'voice', label: 'Voce' },
  { value: 'tool', label: 'Strumenti' },
  { value: 'eval', label: 'Sicurezza' }
];

function rangeFromPreset(preset) {
  const now = new Date();
  if (preset === 'today') {
    const f = new Date(now); f.setUTCHours(0,0,0,0);
    return { from: f, to: now };
  }
  if (preset === '7d') return { from: new Date(now.getTime() - 7*24*60*60*1000), to: now };
  if (preset === '30d') return { from: new Date(now.getTime() - 30*24*60*60*1000), to: now };
  // month
  const f = new Date(now); f.setUTCDate(1); f.setUTCHours(0,0,0,0);
  return { from: f, to: now };
}

const PRESET_TABS = [
  { value: 'month', label: 'Mese corrente' },
  { value: 'today', label: 'Oggi' },
  { value: '7d', label: '7 giorni' },
  { value: '30d', label: '30 giorni' }
];

export function UsagePage() {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('analytics:read');

  const [preset, setPreset] = useState('month');
  const [kind, setKind] = useState('');

  const range = rangeFromPreset(preset);
  const overviewQuery = useQuery({
    queryKey: ['ai', 'usage', 'overview', preset, kind],
    enabled: canRead,
    queryFn: () => aiApi.usageOverview({ kind: kind || undefined, from: range.from.toISOString(), to: range.to.toISOString() })
  });

  const budgetQuery = useQuery({
    queryKey: ['ai', 'budget'],
    enabled: canRead,
    queryFn: aiApi.budgetStatus,
    refetchInterval: 60_000
  });

  const breakerQuery = useQuery({
    queryKey: ['ai', 'breaker'],
    enabled: canRead && hasPermission('system:health'),
    queryFn: aiApi.breakerStatus,
    refetchInterval: 30_000
  });

  const { items, isLoading: listLoading, pagination, setPage } = usePaginatedQuery({
    key: ['ai', 'usage', 'list', preset, kind],
    fetcher: (params) => aiApi.listUsage({
      ...params,
      kind: kind || undefined,
      from: range.from.toISOString(),
      to: range.to.toISOString()
    })
  });

  const totals = overviewQuery.data && overviewQuery.data.totals;
  const byModel = (overviewQuery.data && overviewQuery.data.byModel) || [];
  const budget = budgetQuery.data;

  if (!canRead) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('nav.aiUsage')} />
        <Card>
          <p className="text-sm text-ink-muted">Permesso <code>analytics:read</code> richiesto.</p>
        </Card>
      </div>
    );
  }

  const breakerState = breakerQuery.data;
  const breakerTone = !breakerState ? 'neutral'
    : breakerState.state === 'closed' ? 'success'
      : breakerState.state === 'half_open' ? 'warning' : 'danger';

  const columns = [
    { key: 'createdAt', header: 'Quando', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'kind', header: 'Tipo', render: (r) => <Badge>{r.kind}</Badge> },
    { key: 'model', header: 'Modello', render: (r) => <code className="text-xs">{r.model}</code> },
    { key: 'agentId', header: 'Agente', render: (r) => r.agentId || <span className="text-ink-faint">-</span> },
    { key: 'principal', header: 'Principal', render: (r) => r.principal?.email || r.principal?.id || <span className="text-ink-faint">-</span> },
    { key: 'totalTokens', header: 'Token', align: 'right', render: (r) => fmt.int(r.totalTokens) },
    { key: 'costUsd', header: 'Costo', align: 'right', render: (r) => fmt.usdFine(r.costUsd) },
    { key: 'latencyMs', header: 'Latenza', align: 'right', render: (r) => fmt.durationMs(r.latencyMs) },
    { key: 'errored', header: 'Esito', render: (r) => r.errored ? <Badge tone="danger">errore</Badge> : <Badge tone="success">ok</Badge> }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.aiUsage')}
        description="Consumi AI, budget mensile e stato del circuit breaker."
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Budget mensile"
          loading={budgetQuery.isLoading}
          value={budget ? fmt.usd(budget.spent || 0) : '-'}
          sub={budget && budget.cap ? `su ${fmt.usd(budget.cap)} (${fmt.pct((budget.spent || 0) / (budget.cap || 1))})` : null}
        />
        <KpiCard
          label="Comportamento"
          loading={budgetQuery.isLoading}
          value={budget ? (budget.behaviorOnExceeded === 'block' ? 'Blocco' : 'Notifica') : '-'}
          sub={budget && budget.warnAt ? `Avviso oltre ${budget.warnAt}%` : null}
        />
        <Card className="p-5">
          <p className="text-xs font-medium uppercase tracking-wider text-ink-muted">Circuit breaker</p>
          {breakerQuery.isLoading ? <Skeleton className="mt-2 h-7 w-24" /> : (
            <div className="mt-2 flex flex-col gap-1">
              <Badge tone={breakerTone}>{breakerState?.state || 'unknown'}</Badge>
              {breakerState && (
                <p className="text-xs text-ink-muted">
                  Errori: {fmt.int(breakerState.errorCount || 0)} / soglia {fmt.int(breakerState.errorsThreshold || 0)}
                </p>
              )}
            </div>
          )}
        </Card>
        <KpiCard
          label="Richieste nel periodo"
          loading={overviewQuery.isLoading}
          value={totals ? fmt.int(totals.requests) : '-'}
          sub={totals ? `Errori: ${totals.errors}` : null}
        />
      </section>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs value={preset} onChange={setPreset} items={PRESET_TABS} />
        <Tabs value={kind} onChange={setKind} items={KIND_TABS} />
      </div>

      <Card>
        <h2 className="mb-4 text-base font-semibold text-ink">Per modello</h2>
        {overviewQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : byModel.length === 0 ? (
          <p className="text-sm text-ink-muted">Nessun consumo nel periodo selezionato.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-surface">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Tipo</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Modello</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Richieste</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Token</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Costo</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Errori</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {byModel.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2"><Badge>{row.kind}</Badge></td>
                    <td className="px-3 py-2"><code className="text-xs">{row.model}</code></td>
                    <td className="px-3 py-2 text-right">{fmt.int(row.requests)}</td>
                    <td className="px-3 py-2 text-right">{fmt.int(row.totalTokens)}</td>
                    <td className="px-3 py-2 text-right">{fmt.usdFine(row.costUsd)}</td>
                    <td className="px-3 py-2 text-right">{row.errors > 0 ? <span className="text-warning">{fmt.int(row.errors)}</span> : '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <DataTable
        columns={columns}
        rows={items}
        loading={listLoading}
        empty={{ title: 'Nessuna richiesta', description: 'Le chiamate AI nel periodo appariranno qui.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />
    </div>
  );
}
